import {useMemo} from 'react';

import {
    addToSelection,
    clearSelection as clearSelectionAction,
    removeFromSelection,
} from '../reducers';
import {selectionSelector} from '../selectors';
import {
    useAppDispatch,
    useAppSelector,
} from '../../../hooks/useTypedReactRedux';

import {DnsZone} from '../types';

// FNP-1191 CRUD half — SELECTION.
//
// FNP-1293: this used to be a module-scoped `let globalSelection` plus a `Set`
// of listener callbacks — state living outside React. That shape survived
// unmount and route changes, so an operator who ticked a zone, navigated away
// and came back found the checkbox still ticked against a table that had since
// refetched. It also outlived a test case, which is why it shipped with a
// `resetZonesSelection` export for the suite to call by hand: a workaround for
// the leak rather than a fix for it.
//
// Selection now lives in the Redux slice the module checklist §3 requires,
// built from `SelectionSliceFor` like domain-names, registrars and
// top-level-domains in this same app. Test isolation is structural — the render
// harness builds a fresh store per test — so the hand-rolled reset is gone.
//
// ⚠️ THE HOOK'S CONTRACT IS UNCHANGED on purpose. It still returns
// `selectedRecords` as an ARRAY and still takes a whole array in
// `setSelectedRecords`, because that is exactly what `MantineDataTable`'s
// `selectedRecords` / `onSelectedRecordsChange` pair speaks. The slice stores a
// map keyed by id; this hook is the one place that converts between the two, so
// no caller had to change.
//
// ⚠️ SELECTION IS KEYED ON THE ZONE'S `id`, but the FULL RECORD is stored as the
// value — a zone is addressed by BOTH its id and its connectionId, and the
// delete call needs the connectionId. Storing bare ids would lose it.
export const useZonesSelection = () => {

    const dispatch = useAppDispatch();

    const selection = useAppSelector((state) => selectionSelector(state));

    // The array `MantineDataTable` wants, derived from the slice's map.
    // Memoised on the slice: a new array identity every render would make the
    // table treat an unchanged selection as a change.
    const selectedRecords = useMemo(
        () => Object.values(selection) as DnsZone[],
        [selection],
    );

    // ⚠️ THE TABLE HANDS BACK THE WHOLE NEXT SELECTION, not a delta — it has no
    // add/remove events to offer. So this diffs against what is stored and
    // dispatches only the rows that actually entered or left, rather than
    // clearing and re-adding everything on every tick.
    const setSelectedRecords = (next: DnsZone[]) => {

        const nextIds = new Set(next.map((zone) => zone.id));

        Object.keys(selection)
              .filter((id) => !nextIds.has(id))
              .forEach((id) => dispatch(removeFromSelection(id)));

        next.filter((zone) => typeof selection[zone.id] === 'undefined')
            .forEach((zone) => dispatch(addToSelection(zone)));
    };

    return {
        selectedRecords,
        setSelectedRecords,
        // The DELETE pattern doc's "clear selection after successful deletion".
        clearSelection: () => dispatch(clearSelectionAction()),
    };
};
